import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Banner } from '@osd/ui/banner';
import { Button } from '@osd/ui/button';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { Toast, ToastProvider, useToast } from '@osd/ui/toast';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/toast — live docs for the `Toast` primitive.
 *
 * Toast notifications are transient, dismissable messages. The
 * `<ToastProvider>` mounts a viewport at one of six corners via
 * a React portal; `useToast()` returns an imperative
 * `{ show, dismiss, dismissAll }` API. Wrap your app once near
 * the root and call `show()` from anywhere.
 */
export const Route = createFileRoute('/ui/_layout/toast')({
  component: ToastPage,
});

function ToastPage() {
  return (
    <ToastProvider position="bottom-right">
      <Stack gap={6}>
        <Section
          title="Toast"
          hint="Transient notification — wrap your app in <ToastProvider> + call useToast().show() anywhere"
        >
          <Text>
            The provider mounts a fixed-position viewport via <code>createPortal</code>. Each toast
            auto-dismisses after a configurable duration (default 5s). Use <code>duration: 0</code>{' '}
            for sticky toasts.
          </Text>
        </Section>

        <Section title="Live demo" hint="Click to fire each variant">
          <ToastDemo />
        </Section>

        <Section
          title="Standalone <Toast>"
          hint="Render directly without a provider if you want to compose the viewport yourself"
        >
          <Stack gap={3}>
            <Toast
              variant="info"
              title="Heads up"
              description="This is rendered directly, not via the provider."
              onDismiss={() => undefined}
            />
            <Toast variant="success" title="Saved" />
            <Toast variant="warning" title="Storage almost full" description="87% of quota used." />
          </Stack>
        </Section>

        <Section title="Code example" hint="copy-pasteable usage">
          <Stack gap={5}>
            <CodeBlock
              language="tsx"
              title="Mount once near the root"
              code={`import { ToastProvider } from '@osd/ui/toast';

function App({ children }) {
  return (
    <ToastProvider position="bottom-right">{children}</ToastProvider>
  );
}`}
            />
            <CodeBlock
              language="tsx"
              title="Fire from anywhere via useToast()"
              code={`import { useToast } from '@osd/ui/toast';

function SaveButton() {
  const toast = useToast();
  return (
    <Button
      onClick={async () => {
        await save();
        toast?.show({
          variant: 'success',
          title: 'Saved',
          description: 'Your changes are live.',
        });
      }}
    >
      Save
    </Button>
  );
}`}
            />
            <CodeBlock
              language="tsx"
              title="Update a pending toast in place"
              code={`const id = toast?.show({ title: 'Uploading…', duration: 0 });
await upload();
toast?.show({ id, variant: 'success', title: 'Uploaded' });`}
            />
          </Stack>
        </Section>

        <Banner variant="info" title="Accessibility">
          Danger and warning variants announce via <code>role="alert"</code> (assertive live
          region); info and success use <code>role="status"</code> (polite). The close button is
          labelled &ldquo;Dismiss notification&rdquo; for screen readers.
        </Banner>
      </Stack>
    </ToastProvider>
  );
}

function ToastDemo(): JSX.Element {
  const toast = useToast();
  const [count, setCount] = useState(0);

  return (
    <Stack gap={3}>
      <Stack direction="row" gap={2}>
        <Button
          variant="primary"
          onClick={() => {
            toast?.show({
              variant: 'info',
              title: 'Heads up',
              description: 'New messages have arrived.',
            });
          }}
        >
          Fire info
        </Button>
        <Button
          onClick={() => {
            toast?.show({
              variant: 'success',
              title: 'Saved',
              description: 'Your changes are live.',
            });
          }}
        >
          Fire success
        </Button>
        <Button
          onClick={() => {
            toast?.show({
              variant: 'warning',
              title: 'Quota almost full',
              description: '87% of storage used.',
            });
          }}
        >
          Fire warning
        </Button>
        <Button
          variant="danger"
          onClick={() => {
            toast?.show({
              variant: 'danger',
              title: 'Save failed',
              description: 'Network request timed out.',
            });
          }}
        >
          Fire danger
        </Button>
      </Stack>
      <Stack direction="row" gap={2}>
        <Button
          onClick={() => {
            const id = toast?.show({
              title: `In-flight #${count + 1}`,
              description: 'Replacing in 1.5s…',
              duration: 0,
            });
            setCount((n) => n + 1);
            setTimeout(() => {
              toast?.show({
                id,
                variant: 'success',
                title: `In-flight #${count + 1} done`,
              });
            }, 1500);
          }}
        >
          Fire sticky → update in place
        </Button>
        <Button onClick={() => toast?.dismissAll()}>Dismiss all</Button>
      </Stack>
    </Stack>
  );
}
